import React, { useCallback, useEffect, useRef } from "react";
import { usePlacesWidget } from "react-google-autocomplete";
import { Input } from "@repo/ui/components/ui/input";
import { cn } from "@repo/common/utils/cn";
import { parseGoogleAddressComponents } from "@repo/common/utils/format-address";
import { AddressRequiredFields } from "./types";
import { SUPPORTED_SIGNUP_COUNTRY_CODES_LOWER } from "./constants";
type AddressAutocompleteProps = {
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  // called when user picks a Places suggestion; includes formatted text for reconciliation with the form field
  onAddressConfirm?: (address: AddressRequiredFields, formattedAddress: string) => void;
  sizeVariant?: "md" | "lg";
};
const googlePlacesApiKey = process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY;
if (!googlePlacesApiKey) {
  console.warn("NEXT_PUBLIC_GOOGLE_PLACES_API_KEY is not set. Google Places API will not work.");
}

/** sync browser/autofill DOM value into RHF and poke Places to show predictions */
function nudgePlacesAfterDomUpdate(node: HTMLInputElement | null) {
  requestAnimationFrame(() => {
    if (!node?.isConnected) {
      return;
    }
    node.focus();
    node.dispatchEvent(new InputEvent("input", {
      bubbles: true,
      cancelable: true
    }));
  });
}
export function AddressAutocomplete({
  value,
  onChange,
  placeholder = "Start typing your address...",
  className,
  onAddressConfirm,
  sizeVariant = "md"
}: AddressAutocompleteProps) {
  const handleChange = useCallback((next: string) => {
    onChange?.(next);
  }, [onChange]);
  const {
    ref: placesRef
  } = usePlacesWidget({
    apiKey: googlePlacesApiKey,
    // matches library default — discourages Chrome shipping autofill UI over .pac-container (see SO #49161445 + package source)
    inputAutocompleteValue: "new-password",
    onPlaceSelected: place => {
      if (place?.address_components && place.formatted_address) {
        const addressComponents = parseGoogleAddressComponents(place.address_components);
        const formatted = place.formatted_address;
        handleChange(formatted);
        onAddressConfirm?.(addressComponents as AddressRequiredFields, formatted);
      }
    },
    options: {
      types: ["address"],
      fields: ["formatted_address", "address_components"],
      // Homeworks is only available in these countries — limit suggestions so an
      // unsupported business address can't be selected (ENG-3381).
      componentRestrictions: {
        country: SUPPORTED_SIGNUP_COUNTRY_CODES_LOWER
      }
    }
  });
  const flushDomValueIfStale = useCallback((node: HTMLInputElement | null, options: {
    nudge: boolean;
  }) => {
    if (!node) {
      return;
    }
    const native = node.value;
    if (!native.trim()) {
      return;
    }
    if (native !== value) {
      handleChange(native);
    }
    if (options.nudge) {
      nudgePlacesAfterDomUpdate(node);
    }
  }, [value, handleChange]);
  const flushHandlerRef = useRef(flushDomValueIfStale);
  flushHandlerRef.current = flushDomValueIfStale;
  useEffect(() => {
    const el = (placesRef as React.MutableRefObject<HTMLInputElement | null>).current;
    if (!el) {
      return;
    }
    const onNativeInput = () => {
      flushHandlerRef.current(el, {
        nudge: false
      });
    };
    const onNativeChange = () => {
      flushHandlerRef.current(el, {
        nudge: true
      });
    };
    el.addEventListener("input", onNativeInput);
    el.addEventListener("change", onNativeChange);
    return () => {
      el.removeEventListener("input", onNativeInput);
      el.removeEventListener("change", onNativeChange);
    };
  }, [placesRef]);

  // google Places handles ArrowUp/ArrowDown on the focused input; keep focus there when .pac-container opens
  useEffect(() => {
    if (typeof document === "undefined" || !window.MutationObserver) {
      return;
    }
    let rafOuter = 0;
    let rafInner = 0;
    const scheduleRefocusIfNeeded = () => {
      cancelAnimationFrame(rafOuter);
      cancelAnimationFrame(rafInner);
      rafOuter = requestAnimationFrame(() => {
        rafInner = requestAnimationFrame(() => {
          const input = (placesRef as React.MutableRefObject<HTMLInputElement | null>).current;
          if (!input?.isConnected) {
            return;
          }
          if (document.activeElement === input) {
            return;
          }
          if (!document.querySelector(".pac-container .pac-item")) {
            return;
          }
          input.focus({
            preventScroll: true
          });
        });
      });
    };
    const observer = new MutationObserver(() => {
      if (!document.querySelector(".pac-container .pac-item")) {
        return;
      }
      scheduleRefocusIfNeeded();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafOuter);
      cancelAnimationFrame(rafInner);
    };
  }, [placesRef]);
  return <div className="relative w-full">
      <Input ref={placesRef as React.Ref<HTMLInputElement>} className={cn("signup-address-places-input", className)} value={value} onChange={e => handleChange(e.target.value)} onFocus={e_0 => {
      const inputEl = e_0.currentTarget;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const target = inputEl?.isConnected === true ? inputEl : (placesRef as React.MutableRefObject<HTMLInputElement | null>).current;
          flushHandlerRef.current(target, {
            nudge: true
          });
        });
      });
    }} onAnimationStart={e_1 => {
      if (!e_1.animationName.includes("signupAddressPlacesAutoFill")) {
        return;
      }
      const inputEl_0 = e_1.currentTarget;
      flushHandlerRef.current(inputEl_0 ?? null, {
        nudge: true
      });
    }} placeholder={placeholder} sizeVariant={sizeVariant} />
    </div>;
}